import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import SupportBanner from "@/components/SupportBanner";

export default function PrivacyPolicy() {
  const today = new Date().toLocaleDateString("it-IT");
  return (
    <div className="min-h-screen bg-zen-bg py-10 px-6" data-testid="privacy-page">
      <Seo
        title="Privacy Policy — FreelanZen"
        description="Come FreelanZen raccoglie, utilizza e protegge i tuoi dati personali secondo il GDPR."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-zen-ink2 hover:text-zen-primary text-sm mb-6">
          <ArrowLeft size={14}/> Torna alla home
        </Link>
        <div className="zen-card p-8 md:p-10 space-y-6">
          <div>
            <img src="/brand/freelanzen-wordmark.png" alt="FreelanZen" className="h-8 w-auto mb-4"/>
            <h1 className="font-heading text-3xl md:text-4xl">Privacy Policy</h1>
            <p className="text-zen-ink2 mt-1 text-sm">Ultimo aggiornamento: {today}</p>
          </div>

          <section className="space-y-3 text-sm leading-relaxed text-zen-ink">
            <p>La presente Privacy Policy descrive come <b>FreelanZen</b> raccoglie, utilizza e protegge i dati personali degli utenti che si registrano e utilizzano il servizio, in conformità al Regolamento (UE) 2016/679 (<b>GDPR</b>) e al D.Lgs. 196/2003.</p>

            <h2 className="font-heading text-xl mt-6">1. Titolare del trattamento</h2>
            <p>Il Titolare del trattamento è <b>FreelanZen</b>. Per qualsiasi comunicazione relativa al trattamento dei dati personali puoi contattarci all&apos;indirizzo <a href="mailto:privacy@freelanzen.it" className="text-zen-primary hover:underline">privacy@freelanzen.it</a>.</p>

            <h2 className="font-heading text-xl mt-6">2. Dati raccolti</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li><b>Dati di registrazione</b>: email, nome, password (in forma cifrata bcrypt).</li>
              <li><b>Dati aziendali</b>: P.IVA, codice fiscale, indirizzo, IBAN, logo, per la generazione di fatture e preventivi.</li>
              <li><b>Dati dei clienti</b>: informazioni sui tuoi contatti commerciali (nome, azienda, email, telefono, WhatsApp).</li>
              <li><b>Dati di fatturazione</b>: importi, scadenze, pagamenti, file XML SdI generati.</li>
              <li><b>Credenziali di terze parti</b>: password App Gmail/Outlook/Aruba (cifrate con Fernet).</li>
              <li><b>Metadati tecnici</b>: indirizzo IP, timestamp di accesso, log applicativi (retention 90 giorni).</li>
            </ul>

            <h2 className="font-heading text-xl mt-6">3. Finalità e basi giuridiche</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>Erogazione del servizio (<i>art. 6.1.b GDPR - contratto</i>).</li>
              <li>Fatturazione e adempimenti fiscali (<i>art. 6.1.c GDPR - obbligo di legge</i>).</li>
              <li>Sicurezza e prevenzione abusi (<i>art. 6.1.f GDPR - legittimo interesse</i>).</li>
              <li>Analisi anonima dell&apos;uso, solo con consenso esplicito (<i>art. 6.1.a GDPR</i>).</li>
            </ul>

            <h2 className="font-heading text-xl mt-6">4. Cookie</h2>
            <p>Utilizziamo:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li><b>Cookie tecnici essenziali</b> (session_token, access_token httpOnly) — sempre attivi, necessari per l&apos;autenticazione. Non richiedono consenso.</li>
              <li><b>Cookie di preferenza</b> (fz_ref, cookie_consent) — memorizzati solo nel browser (localStorage). Non condivisi con terzi.</li>
              <li><b>Cookie analitici</b> (opzionali) — attivi solo dopo esplicito consenso tramite il banner. Puoi revocare il consenso in qualsiasi momento cancellando i cookie del browser.</li>
            </ul>

            <h2 className="font-heading text-xl mt-6">5. Fornitori terzi (Data Processors)</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li><b>Stripe, Inc.</b> — elaborazione pagamenti (USA, adesione DPF EU-USA).</li>
              <li><b>PayPal (Europe) S.à r.l. et Cie, S.C.A.</b> — elaborazione pagamenti alternativi (UE, Lussemburgo).</li>
              <li><b>MongoDB Atlas</b> — hosting database (EU-based).</li>
              <li><b>Resend / Emergent Integrations</b> — invio email transazionali.</li>
              <li><b>Anthropic (Claude)</b> — generazione insights AI (opzionale, i dati aggregati sono anonimizzati).</li>
            </ul>
            <p>Tutti i fornitori sono vincolati da accordi di trattamento dati (DPA) conformi al GDPR.</p>
            <p className="text-zen-ink2 text-xs">Nota: il collegamento WhatsApp con i clienti avviene tramite link click-to-chat (<code>wa.me</code>) aperto dal tuo browser: FreelanZen non invia dati a Meta Platforms Ireland Ltd. né legge i messaggi ricevuti.</p>

            <h2 className="font-heading text-xl mt-6">6. Trasferimenti extra-UE</h2>
            <p>Alcuni fornitori (Stripe, Anthropic) possono trattare dati negli Stati Uniti. Il trasferimento avviene sulla base delle Clausole Contrattuali Standard (SCC) approvate dalla Commissione Europea o della certificazione EU-US Data Privacy Framework.</p>

            <h2 className="font-heading text-xl mt-6">7. Conservazione dei dati</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>Dati account e fatture: fino alla cancellazione dell&apos;account + 10 anni (obbligo fiscale italiano).</li>
              <li>Log applicativi: 90 giorni.</li>
              <li>Token di reset password: 1 ora.</li>
              <li>Token di verifica email: 48 ore.</li>
            </ul>

            <h2 className="font-heading text-xl mt-6">8. I tuoi diritti</h2>
            <p>Puoi in qualsiasi momento esercitare i diritti previsti dagli articoli 15-22 del GDPR:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Accesso ai tuoi dati personali</li>
              <li>Rettifica dei dati inesatti</li>
              <li>Cancellazione (&quot;diritto all&apos;oblio&quot;)</li>
              <li>Limitazione del trattamento</li>
              <li>Portabilità dei dati (export CSV/JSON)</li>
              <li>Opposizione al trattamento</li>
              <li>Revoca del consenso in qualsiasi momento</li>
            </ul>
            <p>Per esercitare questi diritti scrivi a <a href="mailto:privacy@freelanzen.it" className="text-zen-primary hover:underline">privacy@freelanzen.it</a>. Risponderemo entro 30 giorni. Hai inoltre il diritto di proporre reclamo al <b>Garante per la protezione dei dati personali</b> (<a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-zen-primary hover:underline">www.garanteprivacy.it</a>).</p>

            <h2 className="font-heading text-xl mt-6">9. Sicurezza</h2>
            <p>Adottiamo misure tecniche e organizzative adeguate per proteggere i tuoi dati: password bcrypt, credenziali terze parti cifrate con Fernet, cookie httpOnly + Secure, rate limiting sugli endpoint sensibili, HTTPS obbligatorio, backup criptati.</p>

            <h2 className="font-heading text-xl mt-6">10. Modifiche</h2>
            <p>Ci riserviamo il diritto di aggiornare questa Privacy Policy. Ti notificheremo eventuali modifiche sostanziali via email almeno 30 giorni prima dell&apos;entrata in vigore.</p>
          </section>
        </div>
      </div>
      <SupportBanner variant="site" />
    </div>
  );
}
